import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { EyeOff, ShieldCheck, Users } from 'lucide-react';

import { SectionCard } from '../../../components/design-system/admin';
import { SettingsToggleRow } from '../../../components/design-system/common';
import { RetailerNetworkSummary, useNetworkApi } from '../../../common/hooks/use-network-api.hook';
import { readApiError } from '../../../common/utils/api-error';

/**
 * What the brands a shop carries get to see, in plain words, and the two
 * switches that decide it.
 *
 * Written for a shop owner deciding whether to trust us, so it says what is
 * shared AND what never is, and it does not oversell: demand is shared only
 * combined with other shops, the shop's name only if they say so. The second
 * switch starts off.
 */
export const DataSharingCard: React.FC<{
    settings: RetailerNetworkSummary['settings'] | null;
    onSaved: (s: RetailerNetworkSummary['settings']) => void;
}> = ({ settings, onSaved }) => {
    const { t, i18n } = useTranslation();
    const api = useNetworkApi();
    const [saving, setSaving] = useState<'shareDemand' | 'listed' | null>(null);

    const save = async (key: 'shareDemand' | 'listed', value: boolean) => {
        setSaving(key);
        try {
            onSaved(await api.updateSettings({ [key]: value }));
            toast.success(t('NETWORK_RETAILER.SHARING.SAVED'));
        } catch (e) {
            toast.error((await readApiError(e)).message || t('NETWORK_RETAILER.ERROR'));
        } finally {
            setSaving(null);
        }
    };

    return (
        <SectionCard
            title={t('NETWORK_RETAILER.SHARING.TITLE')}
            description={t('NETWORK_RETAILER.SHARING.SUBTITLE')}
            testId="network-sharing"
        >
            <div className="flex flex-col gap-6">
                <ul className="flex flex-col gap-3 text-sm">
                    <Point icon={<Users />} title={t('NETWORK_RETAILER.SHARING.SEE_TITLE')} body={t('NETWORK_RETAILER.SHARING.SEE_BODY')} />
                    <Point icon={<ShieldCheck />} title={t('NETWORK_RETAILER.SHARING.COMBINED_TITLE')} body={t('NETWORK_RETAILER.SHARING.COMBINED_BODY')} />
                    <Point icon={<EyeOff />} title={t('NETWORK_RETAILER.SHARING.NEVER_TITLE')} body={t('NETWORK_RETAILER.SHARING.NEVER_BODY')} />
                </ul>

                <div className="flex flex-col gap-5 border-t pt-5">
                    <SettingsToggleRow
                        id="network-share-demand"
                        label={t('NETWORK_RETAILER.SHARING.SHARE_DEMAND')}
                        description={t('NETWORK_RETAILER.SHARING.SHARE_DEMAND_HINT')}
                        checked={settings?.shareDemand ?? true}
                        disabled={!settings || saving !== null}
                        onCheckedChange={(v) => save('shareDemand', v)}
                    />
                    <SettingsToggleRow
                        id="network-listed"
                        label={t('NETWORK_RETAILER.SHARING.LISTED')}
                        description={t('NETWORK_RETAILER.SHARING.LISTED_HINT')}
                        checked={settings?.listed ?? false}
                        disabled={!settings || saving !== null}
                        onCheckedChange={(v) => save('listed', v)}
                    />
                </div>

                {settings?.joinedAt && (
                    <p className="text-xs text-muted-foreground">
                        {t('NETWORK_RETAILER.SHARING.SINCE', {
                            date: new Date(settings.joinedAt).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' }),
                        })}
                    </p>
                )}
            </div>
        </SectionCard>
    );
};

const Point: React.FC<{ icon: React.ReactNode; title: string; body: string }> = ({ icon, title, body }) => (
    <li className="flex items-start gap-3">
        <span className="mt-0.5 flex text-muted-foreground [&_svg]:size-4">{icon}</span>
        <div>
            <p className="font-medium">{title}</p>
            <p className="mt-0.5 text-muted-foreground">{body}</p>
        </div>
    </li>
);
